/**
 * Stale-tab self-refresh (FIX3, 2026-10-03).
 *
 * The SPA polls its *data* forever but never reloaded its own *code*: a tab
 * opened before the OQA P&L fix deploy kept running the old
 * `(mark - basis) x qty` math against new data and showed the female's
 * unrealized P&L as "half a million+", while a fresh load of the same page
 * was correct. Headers were never the problem (index.html is no-cache, the
 * hashed bundles immutable) — nothing ever asked the page to load the new
 * bundle.
 *
 * The build (`vite.config.ts`'s `obs-build-version` plugin) writes
 * `version.json` next to `index.html` (served no-cache under `/observatory/`):
 *
 *     {"schema_version": 1, "entry": "index-<hash>.js", "build": "<16 hex>"}
 *
 * `entry` is the content-hashed entry chunk's file name — exactly the file
 * this running code was loaded from (`import.meta.url`), so a tab knows
 * which build it is with no build-time constant (which would itself perturb
 * the hash). `build` is a digest over every emitted bundle file name, so a
 * CSS- or lazy-chunk-only change is a new build too.
 *
 * `BuildWatcher` re-fetches it on the data poll cadence and whenever the tab
 * becomes visible or focused. On a mismatch it shows a short "Observatory
 * updated — reloading" toast and reloads (`location.reload()` keeps the URL,
 * hash route included). A per-target guard in `sessionStorage` caps reload
 * attempts (a CDN still serving the old `index.html` must never cause a
 * reload loop): at most `MAX_RELOAD_ATTEMPTS` per build within
 * `RELOAD_GUARD_WINDOW_MS`, after which the watcher stays quiet until the
 * window passes.
 */

export interface BuildVersion {
  readonly entry: string;
  readonly build: string;
}

export type BuildCheckResult = "current" | "reload" | "suppressed" | "unknown";

export const RELOAD_GUARD_KEY = "obs-build-reload-guard";
export const MAX_RELOAD_ATTEMPTS = 2;
export const RELOAD_GUARD_WINDOW_MS = 10 * 60_000;
export const RELOAD_TOAST_TEXT = "Observatory updated — reloading";

/** The `version.json` shape, validated (anything else is "unknown", never a reload). */
export function parseBuildVersion(raw: unknown): BuildVersion | null {
  if (typeof raw !== "object" || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const entry = record["entry"];
  const build = record["build"];
  if (typeof entry !== "string" || entry === "" || typeof build !== "string" || build === "") {
    return null;
  }
  return { entry, build };
}

/** The file name a module URL was loaded from (`.../assets/index-abc.js` -> `index-abc.js`). */
export function entryOf(moduleUrl: string): string | null {
  try {
    const path = new URL(moduleUrl).pathname;
    const name = path.slice(path.lastIndexOf("/") + 1);
    return name === "" ? null : name;
  } catch {
    return null;
  }
}

interface ReloadGuard {
  readonly target: string;
  readonly attempts: number;
  readonly at: number;
}

function readGuard(storage: Storage | null): ReloadGuard | null {
  if (storage === null) return null;
  try {
    const raw: unknown = JSON.parse(storage.getItem(RELOAD_GUARD_KEY) ?? "null");
    if (typeof raw !== "object" || raw === null) return null;
    const record = raw as Record<string, unknown>;
    const { target, attempts, at } = record;
    if (typeof target !== "string" || typeof attempts !== "number" || typeof at !== "number") {
      return null;
    }
    return { target, attempts, at };
  } catch {
    return null;
  }
}

function writeGuard(storage: Storage | null, guard: ReloadGuard | null): void {
  if (storage === null) return;
  try {
    if (guard === null) storage.removeItem(RELOAD_GUARD_KEY);
    else storage.setItem(RELOAD_GUARD_KEY, JSON.stringify(guard));
  } catch {
    // Storage full/disabled: the guard degrades to "no memory", and the
    // in-memory `reloading` latch still prevents a reload storm in this tab.
  }
}

export interface BuildWatcherOptions {
  /** The entry file this running code was loaded from (`entryOf(import.meta.url)`). */
  readonly currentEntry: string | null;
  /** Fetches and parses `version.json`; `null` when absent/unparseable. */
  readonly fetchVersion: () => Promise<BuildVersion | null>;
  readonly reload: () => void;
  readonly notify: (message: string) => void;
  readonly storage: Storage | null;
  readonly now?: () => number;
  readonly intervalMs: number;
  /** How long the toast shows before the reload (default 2.5 s). */
  readonly reloadDelayMs?: number;
  /** Where visibility/focus events are observed (default: `document`/`window`). */
  readonly doc?: Document;
  readonly win?: Window;
}

export class BuildWatcher {
  private timer: ReturnType<typeof setInterval> | null = null;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;
  private baselineBuild: string | null = null;
  private reloading = false;
  private inFlight: Promise<BuildCheckResult> | null = null;
  private readonly onVisible = (): void => {
    if (this.doc.visibilityState === "visible") void this.check();
  };
  private readonly onFocus = (): void => {
    void this.check();
  };

  constructor(private readonly options: BuildWatcherOptions) {}

  private get doc(): Document {
    return this.options.doc ?? document;
  }

  private get win(): Window {
    return this.options.win ?? window;
  }

  private now(): number {
    return (this.options.now ?? Date.now)();
  }

  start(): void {
    if (this.timer !== null) return;
    this.timer = setInterval(() => void this.check(), this.options.intervalMs);
    this.doc.addEventListener("visibilitychange", this.onVisible);
    this.win.addEventListener("focus", this.onFocus);
    void this.check();
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    if (this.reloadTimer !== null) clearTimeout(this.reloadTimer);
    this.reloadTimer = null;
    this.doc.removeEventListener("visibilitychange", this.onVisible);
    this.win.removeEventListener("focus", this.onFocus);
  }

  /** One check; concurrent callers (interval + focus + visibility) share it. */
  check(): Promise<BuildCheckResult> {
    if (this.inFlight === null) {
      this.inFlight = this.checkOnce().finally(() => {
        this.inFlight = null;
      });
    }
    return this.inFlight;
  }

  private async checkOnce(): Promise<BuildCheckResult> {
    if (this.reloading) return "reload";
    const current = this.options.currentEntry;
    if (current === null) return "unknown";
    let published: BuildVersion | null;
    try {
      published = await this.options.fetchVersion();
    } catch {
      return "unknown";
    }
    if (published === null) return "unknown";
    const stale =
      published.entry !== current ||
      (this.baselineBuild !== null && published.build !== this.baselineBuild);
    const storage = this.options.storage;
    if (!stale) {
      this.baselineBuild ??= published.build;
      const guard = readGuard(storage);
      if (guard !== null && guard.target === published.build) writeGuard(storage, null);
      return "current";
    }
    const now = this.now();
    const guard = readGuard(storage);
    const sameTarget = guard !== null && guard.target === published.build;
    const withinWindow = sameTarget && now - guard.at < RELOAD_GUARD_WINDOW_MS;
    if (withinWindow && guard.attempts >= MAX_RELOAD_ATTEMPTS) return "suppressed";
    writeGuard(storage, {
      target: published.build,
      attempts: withinWindow ? guard.attempts + 1 : 1,
      at: withinWindow ? guard.at : now,
    });
    this.reloading = true;
    this.options.notify(RELOAD_TOAST_TEXT);
    this.reloadTimer = setTimeout(() => this.options.reload(), this.options.reloadDelayMs ?? 2500);
    return "reload";
  }
}

/** `version.json` under the page's own base path, never from any cache. */
export async function fetchBuildVersion(baseUrl: string): Promise<BuildVersion | null> {
  const response = await fetch(`${baseUrl}version.json?t=${Date.now()}`, { cache: "no-store" });
  if (!response.ok) return null;
  try {
    return parseBuildVersion(await response.json());
  } catch {
    return null;
  }
}

/** A small fixed toast (bottom-centre) — the only UI this module owns. */
export function showUpdateToast(message: string, doc: Document = document): HTMLElement {
  const toast = doc.createElement("div");
  toast.className = "obs-update-toast";
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.textContent = message;
  doc.body.appendChild(toast);
  return toast;
}
